import { getAccessToken } from "../../shared/utils/secureTokenStorage";

const API_BASE = process.env.REACT_APP_API_BASE || "";

/**
 * Marketing Assistant Service
 * Handles all API calls for the Marketing Assistant feature
 */

/**
 * Get skills grouped by category
 * @returns {Promise<{categories: Record<string, MarketingSkill[]>}>}
 */
export const fetchSkillsByCategory = async () => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/marketing/skills/by-category`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 403) {
      const data = await response.json();
      throw new Error(data.detail || "SUBSCRIPTION_REQUIRED");
    }
    throw new Error("Failed to fetch skills");
  }

  return response.json();
};

/**
 * Get single skill details
 * @param {string} skillId
 * @returns {Promise<MarketingSkill>}
 */
export const fetchSkillDetails = async (skillId) => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/marketing/skills/${skillId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch skill details");
  }

  return response.json();
};

/**
 * Get suggested prompts for a skill
 * @param {string} skillId
 * @returns {Promise<{skill_id: string, prompts: string[]}>}
 */
export const fetchSkillPrompts = async (skillId) => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/marketing/skills/${skillId}/prompts`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch skill prompts");
  }

  return response.json();
};

/**
 * Get all categories with skill counts
 * @returns {Promise<{categories: Array<{id: string, name: string, skill_count: number}>}>}
 */
export const fetchCategories = async () => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/marketing/categories`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch categories");
  }

  return response.json();
};

/**
 * Generate a unique session ID for marketing chat
 * @param {string[]} skillIds - Selected skill IDs
 * @returns {string}
 */
export const generateSessionId = (skillIds = []) => {
  const skillHash = skillIds.length > 0 ? skillIds.sort().join("-") : "general";
  return `marketing-${skillHash}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

/**
 * Send a message to the Marketing Assistant (streaming)
 * @param {Object} params
 * @param {string} params.message - User's question
 * @param {string} params.sessionId - Conversation session ID
 * @param {string[]} params.skillIds - Selected skill IDs (empty = search all)
 * @param {string} params.additionalInstructions - Extra context
 * @param {Function} params.onCreditInfo - Callback for credit info (credits_charged, credits_remaining)
 * @param {Function} params.onThinkingStep - Callback for thinking steps
 * @param {Function} params.onChunk - Callback for response chunks
 * @param {Function} params.onToolCall - Callback for tool calls
 * @param {Function} params.onToolResult - Callback for tool results
 * @param {Function} params.onDone - Callback when complete
 * @param {Function} params.onError - Callback for errors
 * @param {Function} params.onInsufficientCredits - Callback for insufficient credits error
 * @returns {Promise<void>}
 */
export const sendMarketingMessage = async ({
  message,
  sessionId,
  skillIds = [],
  additionalInstructions = "",
  onCreditInfo,
  onThinkingStep,
  onChunk,
  onToolCall,
  onToolResult,
  onDone,
  onError,
  onInsufficientCredits,
}) => {
  const token = getAccessToken();

  try {
    const response = await fetch(`${API_BASE}/api/chat/v2/ask/stream`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        message,
        session_id: sessionId,
        mode: "marketing",
        skill_ids: skillIds,
        additional_instructions: additionalInstructions,
      }),
    });

    if (!response.ok) {
      if (response.status === 402) {
        // Insufficient credits
        const data = await response.json();
        const error = new Error("INSUFFICIENT_CREDITS");
        error.creditsRequired = data.credits_required || 100;
        error.creditsAvailable = data.credits_available || 0;
        error.message = data.message || "Insufficient credits";
        onInsufficientCredits?.({
          required: error.creditsRequired,
          available: error.creditsAvailable,
          message: error.message,
        });
        throw error;
      }
      if (response.status === 403) {
        const data = await response.json();
        throw new Error(data.detail || "SUBSCRIPTION_REQUIRED");
      }
      if (response.status === 503) {
        throw new Error("SERVICE_UNAVAILABLE");
      }
      throw new Error("Failed to send message");
    }

    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      throw new Error("No response body");
    }

    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("data: ")) {
          try {
            const event = JSON.parse(line.slice(6));

            switch (event.type) {
              case "credit_info":
                onCreditInfo?.({
                  creditsCharged: event.credits_charged,
                  creditsRemaining: event.credits_remaining,
                });
                break;
              case "thinking_step":
                onThinkingStep?.(event);
                break;
              case "tool_call":
                onToolCall?.(event);
                break;
              case "tool_result":
                onToolResult?.(event);
                break;
              case "chunk":
                onChunk?.(event.content);
                break;
              case "done":
                onDone?.(event);
                break;
              case "error":
                if (event.error === "insufficient_credits") {
                  onInsufficientCredits?.({
                    required: event.credits_required || 100,
                    available: event.credits_available || 0,
                    message: event.message || "Insufficient credits",
                  });
                }
                onError?.(new Error(event.error));
                break;
              default:
                break;
            }
          } catch (parseError) {
            console.warn("Failed to parse SSE event:", line);
          }
        }
      }
    }
  } catch (error) {
    onError?.(error);
    throw error;
  }
};

/**
 * Send a message to the Marketing Assistant (non-streaming)
 * @param {Object} params
 * @param {string} params.message
 * @param {string} params.sessionId
 * @param {string[]} params.skillIds
 * @param {string} params.additionalInstructions
 * @returns {Promise<{response: string, session_id: string, tool_calls: Array}>}
 */
export const sendMarketingMessageSync = async ({
  message,
  sessionId,
  skillIds = [],
  additionalInstructions = "",
}) => {
  const token = getAccessToken();

  const response = await fetch(`${API_BASE}/api/chat/v2/ask`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      message,
      session_id: sessionId,
      mode: "marketing",
      skill_ids: skillIds,
      additional_instructions: additionalInstructions,
    }),
  });

  if (!response.ok) {
    if (response.status === 403) {
      const data = await response.json();
      throw new Error(data.detail || "SUBSCRIPTION_REQUIRED");
    }
    if (response.status === 503) {
      throw new Error("SERVICE_UNAVAILABLE");
    }
    throw new Error("Failed to send message");
  }

  return response.json();
};

/**
 * Check if user has access to Marketing Assistant
 * Requires Pro, Ultra, or Enterprise subscription
 * @param {Object} subscription - User's subscription data
 * @returns {boolean}
 */
export const hasMarketingAccess = (subscription) => {
  // Option 1: Check features array (recommended)
  const features = subscription?.plan?.features || [];
  const marketingFeature = features.find((f) => f.key === "marketing_assistant");
  if (marketingFeature) {
    return marketingFeature.active === true;
  }

  // Option 2: Fallback to tier check
  const MARKETING_ALLOWED_TIERS = ["pro", "ultra", "enterprise"];
  const tier = subscription?.tier?.toLowerCase() || subscription?.plan_id?.toLowerCase() || "free";
  return MARKETING_ALLOWED_TIERS.includes(tier);
};

// ============================================
// Chat History Functions
// ============================================

/**
 * List all Marketing Assistant chat sessions
 * @returns {Promise<{total_count: number, sessions: Array}>}
 */
export const fetchMarketingSessions = async () => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/chat/v2/history/sessions?mode=marketing`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to fetch chat sessions");
  }

  return response.json();
};

/**
 * Load a specific chat session with all messages
 * @param {string} sessionId
 * @returns {Promise<{session_id: string, message_count: number, messages: Array}>}
 */
export const loadMarketingSession = async (sessionId) => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/chat/v2/history/sessions/${sessionId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Session not found");
    }
    throw new Error("Failed to load chat session");
  }

  return response.json();
};

/**
 * Delete a chat session
 * @param {string} sessionId
 * @returns {Promise<void>}
 */
export const deleteMarketingSession = async (sessionId) => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/chat/v2/history/sessions/${sessionId}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error("Failed to delete chat session");
  }
};

/**
 * Search within Marketing Assistant chat history
 * @param {string} query - Search query
 * @returns {Promise<{results: Array}>}
 */
export const searchMarketingHistory = async (query) => {
  const token = getAccessToken();
  const response = await fetch(
    `${API_BASE}/api/chat/v2/history/search?q=${encodeURIComponent(query)}&mode=marketing`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to search chat history");
  }

  return response.json();
};

// Category metadata for UI display
export const CATEGORY_META = {
  seo: {
    icon: "Search",
    color: "emerald",
    gradient: "from-emerald-500 to-teal-600",
  },
  copywriting: {
    icon: "PenTool",
    color: "violet",
    gradient: "from-violet-500 to-purple-600",
  },
  cro: {
    icon: "Target",
    color: "orange",
    gradient: "from-orange-500 to-amber-600",
  },
  strategy: {
    icon: "Lightbulb",
    color: "amber",
    gradient: "from-amber-500 to-yellow-600",
  },
  growth: {
    icon: "Rocket",
    color: "sky",
    gradient: "from-sky-500 to-blue-600",
  },
  general: {
    icon: "Sparkles",
    color: "rose",
    gradient: "from-rose-500 to-pink-600",
  },
};

// ============================================
// Marketing Brief Functions
// ============================================

/**
 * Maximum character limit for marketing brief
 */
export const BRIEF_MAX_CHARS = 50000;

/**
 * Fetch the user's marketing brief
 * @returns {Promise<{user_id: string, content: string, created_at: string, updated_at: string} | null>}
 */
export const fetchMarketingBrief = async () => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/marketing/brief`, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error("Failed to fetch marketing brief");
  }

  const data = await response.json();
  // API returns null if no brief exists
  return data || null;
};

/**
 * Save or update the user's marketing brief
 * @param {string} content - The markdown content of the brief
 * @returns {Promise<{user_id: string, content: string, created_at: string, updated_at: string}>}
 */
export const saveMarketingBrief = async (content) => {
  const token = getAccessToken();
  const response = await fetch(`${API_BASE}/api/marketing/brief`, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ content }),
  });

  if (!response.ok) {
    if (response.status === 413) {
      throw new Error("Brief content exceeds maximum length");
    }
    throw new Error("Failed to save marketing brief");
  }

  return response.json();
};

/**
 * Default template for new marketing briefs
 */
export const BRIEF_TEMPLATE = `# Company Name

## What We Do
Brief description of your product/service.

## Target Audience
- Primary: [who]
- Secondary: [who]

## Core Value Proposition
- Key benefit 1
- Key benefit 2
- Key benefit 3

## Pricing
- Basic: $X/mo
- Pro: $Y/mo

## Brand Voice
Professional, casual, playful, etc.

## Key Messages
- Message 1
- Message 2

## Hashtags & Keywords
#YourHashtag #Industry #Keywords
`;
